import { useState, useMemo } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Star } from "lucide-react"
import { cn } from "@/lib/utils"
import { EmptyState, CountryCell } from "./-components"

export const Route = createFileRoute("/(app)/app/results/specialist")({
  component: SpecialistResultsPage,
})

// Topic order as specified by client, with Mystery Box first (but excluded from Overall)
const TOPIC_ORDER = [
  "Mystery Box",
  "History",
  "Pop Music",
  "Visual Arts",
  "Lifestyle",
  "Films & TV",
  "Geography",
  "Science",
  "Sports",
  "Literature",
  "Performing Arts",
] as const

// Abbreviations for Overall (HI for History, GE for Geography per client request)
const TOPIC_ABBREV: Record<string, string> = {
  "History": "HI",
  "Pop Music": "PM",
  "Visual Arts": "VA",
  "Lifestyle": "LF",
  "Films & TV": "FT",
  "Geography": "GE",
  "Science": "SC",
  "Sports": "SP",
  "Literature": "LT",
  "Performing Arts": "PA",
}

// Topics included in Overall (excludes Mystery Box)
const CIRCUIT_TOPICS = TOPIC_ORDER.filter((t) => t !== "Mystery Box")

function SpecialistResultsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  const selectedTopic = search.topic ?? "circuit"

  const handleTabChange = (value: string) => {
    void navigate({
      to: "/app/results/specialist",
      search: {
        ...search,
        topic: value === "circuit" ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-6">
      <Tabs value={selectedTopic} onValueChange={handleTabChange}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="circuit" className="font-semibold">
            Overall
          </TabsTrigger>
          <span className="w-px bg-border mx-1 h-6 self-center" />
          {TOPIC_ORDER.map((topic) => (
            <TabsTrigger key={topic} value={topic}>
              {topic}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Overall Tab */}
        <TabsContent value="circuit" className="mt-6">
          <CircuitOverallTable />
        </TabsContent>

        {/* Per-Topic Tabs */}
        {TOPIC_ORDER.map((topic) => (
          <TabsContent key={topic} value={topic} className="mt-6">
            {selectedTopic === topic && (
              <TopicLeaderboard topic={topic} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

// Overall - Sum of Best 3 from each topic (excludes Mystery Box)
function CircuitOverallTable() {
  const [sortKey, setSortKey] = useState<"total" | "name">("total")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  // Fetch data for each topic to get best 3 scores
  const historyData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "History",
  })
  const popMusicData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Pop Music",
  })
  const visualArtsData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Visual Arts",
  })
  const lifestyleData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Lifestyle",
  })
  const filmsTvData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Films & TV",
  })
  const geographyData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Geography",
  })
  const scienceData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Science",
  })
  const sportsData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Sports",
  })
  const literatureData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Literature",
  })
  const performingArtsData = useQuery(api.quizzes.handlers.getSpecialistOverallByTopic, {
    topic: "Performing Arts",
  })

  const isLoading =
    historyData === undefined ||
    popMusicData === undefined ||
    visualArtsData === undefined ||
    lifestyleData === undefined ||
    filmsTvData === undefined ||
    geographyData === undefined ||
    scienceData === undefined ||
    sportsData === undefined ||
    literatureData === undefined ||
    performingArtsData === undefined

  // Combine all topic data into overall
  const circuitData = useMemo(() => {
    if (isLoading) return []

    const topicDataMap: Record<string, typeof historyData> = {
      "History": historyData,
      "Pop Music": popMusicData,
      "Visual Arts": visualArtsData,
      "Lifestyle": lifestyleData,
      "Films & TV": filmsTvData,
      "Geography": geographyData,
      "Science": scienceData,
      "Sports": sportsData,
      "Literature": literatureData,
      "Performing Arts": performingArtsData,
    }

    // Build a map of userId -> { best3 per topic }
    const playerMap = new Map<
      string,
      {
        userId: string
        name: string
        country: string | null
        topicBest3: Record<string, number>
      }
    >()

    for (const topic of CIRCUIT_TOPICS) {
      const data = topicDataMap[topic]
      if (!data) continue

      for (const player of data.players) {
        const existing = playerMap.get(player.userId)
        if (existing) {
          existing.topicBest3[topic] = player.best3
        } else {
          playerMap.set(player.userId, {
            userId: player.userId,
            name: player.name,
            country: player.country ?? null,
            topicBest3: { [topic]: player.best3 },
          })
        }
      }
    }

    // Calculate circuit total (sum of all topic best3s)
    const players = Array.from(playerMap.values()).map((p) => {
      const total = Object.values(p.topicBest3).reduce((sum, v) => sum + v, 0)
      return { ...p, total }
    })

    return players
  }, [
    isLoading,
    historyData,
    popMusicData,
    visualArtsData,
    lifestyleData,
    filmsTvData,
    geographyData,
    scienceData,
    sportsData,
    literatureData,
    performingArtsData,
  ])

  const handleSort = (key: "total" | "name") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  const sortedPlayers = useMemo(() => {
    return [...circuitData].sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.name.localeCompare(b.name)
        return sortDir === "asc" ? cmp : -cmp
      }
      return sortDir === "desc" ? b.total - a.total : a.total - b.total
    })
  }, [circuitData, sortKey, sortDir])

  if (isLoading) {
    return <Skeleton className="h-[400px] w-full" />
  }

  if (circuitData.length === 0) {
    return (
      <EmptyState
        title="Specialist Overall"
        icon={Star}
        heading="No Results Yet"
        description="No published specialist quiz results found."
      />
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Specialist Overall</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="sticky left-0 bg-background z-10 min-w-[180px] cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort("name")}
              >
                <span className="flex items-center gap-1">
                  Player {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              <TableHead
                className="text-right min-w-[80px] font-bold bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-100/50"
                onClick={() => handleSort("total")}
              >
                <span className="flex items-center justify-end gap-1">
                  Total {sortKey === "total" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              {CIRCUIT_TOPICS.map((topic) => (
                <TableHead
                  key={topic}
                  className="text-center min-w-[50px]"
                  title={topic}
                >
                  {TOPIC_ABBREV[topic]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPlayers.map((player) => (
              <TableRow key={player.userId}>
                <TableCell className="font-medium sticky left-0 bg-background z-10">
                  {player.name}
                  <CountryCell country={player.country} />
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                  {player.total}
                </TableCell>
                {CIRCUIT_TOPICS.map((topic) => (
                  <TableCell
                    key={topic}
                    className="text-center tabular-nums"
                  >
                    {player.topicBest3[topic] !== undefined ? (
                      player.topicBest3[topic]
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// Per-Topic Leaderboard with #1, #2, #3, #4 columns
function TopicLeaderboard({
  topic,
}: {
  topic: string
}) {
  const [sortKey, setSortKey] = useState<"total" | "name">("total")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  // Get topic standings with per-game scores
  const topicData = useQuery(api.quizzes.handlers.getSpecialistTopicWithGameScores, {
    topic,
  })

  const handleSort = (key: "total" | "name") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  const sortedPlayers = useMemo(() => {
    if (!topicData) return []
    return [...topicData.players].sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.name.localeCompare(b.name)
        return sortDir === "asc" ? cmp : -cmp
      }
      return sortDir === "desc" ? b.best3 - a.best3 : a.best3 - b.best3
    })
  }, [topicData, sortKey, sortDir])

  if (topicData === undefined) {
    return <Skeleton className="h-[400px] w-full" />
  }

  if (topicData.players.length === 0) {
    return (
      <EmptyState
        title={`${topic} Standings`}
        icon={Star}
        heading="No Results Yet"
        description={`No published ${topic} quiz results found.`}
      />
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{topic} Standings</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="sticky left-0 bg-background z-10 min-w-[180px] cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort("name")}
              >
                <span className="flex items-center gap-1">
                  Player {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              <TableHead
                className="text-right min-w-[80px] font-bold bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-100/50"
                onClick={() => handleSort("total")}
              >
                <span className="flex items-center justify-end gap-1">
                  Total {sortKey === "total" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              {/* Game columns - #1, #2, #3, #4 */}
              {topicData.quizzes.map((quiz) => (
                <TableHead
                  key={quiz._id}
                  className="text-center min-w-[50px]"
                >
                  #{quiz.gameNumber}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPlayers.map((player) => (
              <TableRow key={player.userId}>
                <TableCell className="font-medium sticky left-0 bg-background z-10">
                  {player.name}
                  <CountryCell country={player.country} />
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                  {player.best3}
                </TableCell>
                {player.gameScores.map((game) => (
                  <TableCell
                    key={game.quizId}
                    className={cn(
                      "text-center tabular-nums",
                      game.score !== null && game.countsForBest3
                        ? "font-semibold text-foreground"
                        : game.score !== null
                          ? "text-muted-foreground"
                          : ""
                    )}
                  >
                    {game.score !== null ? (
                      game.score
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
